import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Tooltip,
	Legend,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	Line,
	LineChart,
	Area,
	AreaChart,
	LabelList,
	Bar,
	BarChart,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import { analyticsAPI } from '../../../services/constants';
import { request } from '../../../services/utilities';
import { notifyError } from '../../../services/notify';
import ImmunizationOverview from '../../Doctor/ImmunizationOverview';
import moment from 'moment';
import NICUOverview from '../NicuAnalytics';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';

const NurseOverviewModal = ({ selectedStaff }) => {
	const [loading, setLoading] = useState({
		top10Requests: true,
		bottom10Requests: true,
		taskTrends: true,
		taskTrendType: true,
		top10NurseRequest: true,
		bottom10NurseRequest: true,
		top10NurseTask: true,
		bottom10NurseTask: true,
		taskHour: true,
		top10taskType: true,
		bottom10TaskType: true,
		patientRequests: true,
		labourDelivery: true,
		patientAdmission: true,
		kpi: true,
		trend: true,
		summary: true,
		moved: true,
		requisitionTrend: true,
		stats: true,
		value: true,
	});
	const [filters, setFilters] = useState({
		top10Requests: { dateFilter: 'past-month' },
		bottom10Requests: { dateFilter: 'past-month' },
		taskTrends: { dateFilter: 'past-month' },
		taskTrendType: { dateFilter: 'past-month' },
		top10NurseRequest: { dateFilter: 'past-month' },
		bottom10NurseRequest: { dateFilter: 'past-month' },
		top10NurseTask: { dateFilter: 'past-month' },
		bottom10NurseTask: { dateFilter: 'past-month' },
		taskHour: { dateFilter: 'past-month' },
		top10taskType: { dateFilter: 'past-month' },
		bottom10TaskType: { dateFilter: 'past-month' },
		patientRequests: { dateFilter: 'past-month' },
		labourDelivery: { dateFilter: 'past-month' },
		patientAdmission: { dateFilter: 'past-month' },
		requestMetrics: { dateFilter: 'past-month' },
	});

	const prevFiltersRef = useRef(null);

	const [top10Requests, setTop10Requests] = useState([]);
	const [bottom10Requests, setBottom10Requests] = useState([]);
	const [taskTrends, setTaskTrends] = useState([]);
	const [taskTrendType, setTaskTrendType] = useState([]);
	const [top10NurseRequest, setTop10NurseRequest] = useState([]);
	const [bottom10NurserRequest, setBottom10NurserRequest] = useState([]);
	const [top10NurseTask, setTop10NurseTask] = useState([]);
	const [bottom10NurseTask, setBottom10NurseTask] = useState([]);
	const [taskHour, setTaskHour] = useState([]);
	const [top10taskType, setTop10taskType] = useState([]);
	const [bottom10TaskType, setBottom10TaskType] = useState([]);
	const [patientRequests, setPatientRequests] = useState([]);
	const [labourDelivery, setLabourDelivery] = useState([]);
	const [patientAdmission, setPatientAdmission] = useState([]);
	const [valueTrend, setValueTrend] = useState([]);
	const [activeTab, setActiveTab] = useState('nurse');

	const [requestMetrics, setRequestMetrics] = useState({
		totalRequests: '0',
		notReceived: null,
		received: null,
		notSubstituted: null,
		substituted: null,
		dateScheduled: null,
		dateNotScheduled: null,
		notCancelled: null,
		cancelled: null,
		notApproved: null,
		approved: null,
		notFilled: null,
		filled: null,
		deleted: null,
		cancelledOnly: null,
		totalCancelled: 0,
		deletedCount: 0,
		cancelledOnlyCount: 0,
	});
	const [modal, setModal] = useState(false);
	const [modalAlt, setModalAlt] = useState(false);

	const COLORS = [
		'#0088FE', // blue
		'#00C49F', // green
		'#FFBB28', // yellow
		'#FF8042', // orange
		'#8884d8', // purple
		'#FF6699', // pink
		'#A28EFF', // light purple
		'#33CCFF', // sky blue
		'#FF4444', // red
		'#22BB33', // dark green
	];

	const onFilterChange = (filterKey, value) => {
		setFilters(prev => ({
			...prev,
			[filterKey]: {
				...(prev[filterKey] || {}),
				dateFilter: value,
			},
		}));
	};

	const TimeRangeDropdown = ({ chartId, value }) => {
		return (
			<div className="mb-4">
				<form className="form-inline justify-content-sm-end">
					<select
						className="form-control form-control-sm rounded"
						onChange={e => {
							onFilterChange(chartId, e.target.value);
						}}
						value={value}
					>
						<option value="today">Today</option>
						<option value="past-week">Past Week</option>
						<option value="past-month">Past 30 Days</option>
						<option value="last-month">Last Month</option>
						<option value="past-quarter">Past Quarter</option>
						<option value="past-six-months">Past Six Months</option>
						<option value="past-year">Past Year</option>
						<option value="last-year">Last Year</option>
						<option value="past-three-years">Past Three Years</option>
						<option value="all-time">All Time</option>
					</select>
				</form>
			</div>
		);
	};

	// fetch top patients request based on current filter;
	const fetchTopPatientsRequests = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-type?dateFilter=${filters?.top10Requests?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.top10Requests?.dateFilter]);

	// fetch bottom patients request based on current filter;
	const fetchBottomPatientsRequests = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-type?dateFilter=${filters?.bottom10Requests?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.bottom10Requests?.dateFilter]);

	// fetch top patients request based on current filter;
	const fetchPatientsRequests = useCallback(async () => {
		const url = `${analyticsAPI}/nurse-patient?dateFilter=${filters?.patientRequests?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.patientRequests?.dateFilter]);

	// fetch task trends based on current filter;
	const fetchTaskTrends = useCallback(async () => {
		const url = `${analyticsAPI}/clinical-task-completion-trend?dateFilter=${filters?.taskTrends?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.taskTrends?.dateFilter]);

	// fetch task trend type based on current filter;
	const fetchTaskTrendType = useCallback(async () => {
		const url = `${analyticsAPI}/clinical-task-trends?dateFilter=${filters?.taskTrendType?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.taskTrendType?.dateFilter]);

	// fetch top 10 nurse request based on current filter;
	const fetchTopNurseRequests = useCallback(async () => {
		const url = `${analyticsAPI}/requests-by-nurse?dateFilter=${filters?.top10NurseRequest?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.top10NurseRequest?.dateFilter]);

	// fetch bottom 10 nurse request based on current filter;
	const fetchBottomNurseRequests = useCallback(async () => {
		const url = `${analyticsAPI}/requests-by-nurse?dateFilter=${filters?.bottom10NurseRequest?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.bottom10NurseRequest?.dateFilter]);

	// fetch top 10 nurse task based on current filter;
	const fetchTop10NurseTask = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-nurse?dateFilter=${filters?.top10NurseTask?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.top10NurseTask?.dateFilter]);

	// fetch bottom 10 nurse request based on current filter;
	const fetchBottom10NurseTask = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-nurse?dateFilter=${filters?.bottom10NurseTask?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.bottom10NurseTask?.dateFilter]);

	// fetch task per hour based on current filter;
	const fetchTaskHour = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-hour?dateFilter=${filters?.taskHour?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.taskHour?.dateFilter]);

	// fetch bottom 10 task type based on current filter;
	const fetchTop10TaskType = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-type?dateFilter=${filters?.top10taskType?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.top10taskType?.dateFilter]);

	// fetch bottom 10 task type based on current filter;
	const fetchBottom10TaskType = useCallback(async () => {
		const url = `${analyticsAPI}/tasks-by-type?dateFilter=${filters?.bottom10TaskType?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.bottom10TaskType?.dateFilter]);

	// fetch labour delivery based on current filter;
	const fetchLabourDelivery = useCallback(async () => {
		const url = `${analyticsAPI}/labour-delivery-trends?dateFilter=${filters?.labourDelivery?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.labourDelivery?.dateFilter]);

	// fetch labour delivery based on current filter;
	const fetchPatientAdmission = useCallback(async () => {
		const url = `${analyticsAPI}/patient-admission-health-state?dateFilter=${filters?.patientAdmission?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.patientAdmission?.dateFilter]);

	//fetch value trend based on current filter
	const fetchValueTrend = useCallback(async () => {
		const url = `${analyticsAPI}/inventory-value-trend?dateFilter=${filters?.value?.dateFilter}&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs || [];
	}, [filters?.value?.dateFilter]);

	const fetchRequestMetrics = useCallback(async () => {
		const url = `${analyticsAPI}/patient-request-metrics?dateFilter=${filters?.requestMetrics?.dateFilter}&requestType=nursing-service&staffId=${selectedStaff?.id}`;
		const rs = await request(url, 'GET', true);
		return rs?.result || {};
	}, [filters?.requestMetrics?.dateFilter]);

	//
	useEffect(() => {
		const prevFilters = prevFiltersRef.current;

		// Handle requestMetrics separately
		const currentMetricsFilter = filters?.requestMetrics?.dateFilter;
		const prevMetricsFilter = prevFilters?.requestMetrics?.dateFilter;

		if (currentMetricsFilter && (!prevMetricsFilter || currentMetricsFilter !== prevMetricsFilter)) {
			setLoading(prev => ({ ...prev, kpi: true }));

			fetchRequestMetrics()
				.then(data => setRequestMetrics(data))
				.catch(() => notifyError('Error fetching request metrics'))
				.finally(() => setLoading(prev => ({ ...prev, kpi: false })));
		}

		const fetchMap = {
			top10Requests: fetchTopPatientsRequests,
			bottom10Requests: fetchBottomPatientsRequests,
			taskTrends: fetchTaskTrends,
			taskTrendType: fetchTaskTrendType,
			top10NurseRequest: fetchTopNurseRequests,
			bottom10NurseRequest: fetchBottomNurseRequests,
			top10NurseTask: fetchTop10NurseTask,
			bottom10NurseTask: fetchBottom10NurseTask,
			taskHour: fetchTaskHour,
			top10taskType: fetchTop10TaskType,
			bottom10TaskType: fetchBottom10TaskType,
			patientRequests: fetchPatientsRequests,
			labourDelivery: fetchLabourDelivery,
			valueTrend: fetchValueTrend,
			patientAdmission: fetchPatientAdmission,
		};

		Object.entries(fetchMap).forEach(([key, fetchFn]) => {
			const currentFilter = filters?.[key]?.dateFilter;
			const prevFilter = prevFilters?.[key]?.dateFilter;

			if (currentFilter && (!prevFilter || currentFilter !== prevFilter)) {
				setLoading(prev => ({ ...prev, [key]: true }));

				fetchFn()
					.then(data => {
						if (key === 'top10Requests') {
							setTop10Requests(data);
						} else if (key === 'bottom10Requests') {
							setBottom10Requests(data);
						} else if (key === 'taskTrends') {
							setTaskTrends(data);
						} else if (key === 'taskTrendType') {
							setTaskTrendType(data);
						} else if (key === 'top10NurseRequest') {
							setTop10NurseRequest(data);
						} else if (key === 'bottom10NurseRequest') {
							setBottom10NurserRequest(data);
						} else if (key === 'top10NurseTask') {
							setTop10NurseTask(data);
						} else if (key === 'bottom10NurseTask') {
							setBottom10NurseTask(data);
						} else if (key === 'taskHour') {
							setTaskHour(data);
						} else if (key === 'top10taskType') {
							setTop10taskType(data);
						} else if (key === 'bottom10TaskType') {
							setBottom10TaskType(data);
						} else if (key === 'patientRequests') {
							setPatientRequests(data);
						} else if (key === 'labourDelivery') {
							setLabourDelivery(data);
						} else if (key === 'patientAdmission') {
							setPatientAdmission(data);
						}
					})
					.catch(error => notifyError('Error fetching request count'))
					.finally(() => setLoading(prev => ({ ...prev, [key]: false })));
			}
		});

		prevFiltersRef.current = filters;
	}, [filters]);

	//All chart data(s)

	//task-trend-chart
	const TaskTrendChart = taskTrends?.result?.map(item => ({
		name: item?.date || item?.year || item?.month || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const Top10NurseRequestChart = top10NurseRequest?.result?.map(item => ({
		name: item?.req_createdBy || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const Bottom10NurseRequestChart = bottom10NurserRequest?.result?.map(item => ({
		name: item?.req_createdBy || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const PatientRequestsChart =
		patientRequests?.top10
			// ?.sort((a, b) => b.total - a.total)
			?.sort(() => Math.random() - 0.5)
			?.map(item => ({
				name: item?.name || 'Unknown',
				count: parseInt(item?.count, 10),
			}))
			.slice(0, 15) || [];

	const Top10NurseTaskChart = top10NurseTask?.result
		?.sort(() => Math.random() - 0.5)
		?.map(item => ({
			name: item?.req_createdBy || 'Unknown',
			count: parseInt(item?.count, 10),
		}));

	const Bottom10NurseTaskChart = bottom10NurseTask?.result
		?.sort(() => Math.random() - 0.5)
		?.map(item => ({
			name: item?.req_createdBy || 'Unknown',
			count: parseInt(item?.count),
		}));

	// const convertT

	const TaskHourChart = taskHour?.result?.map(item => ({
		// name: `${item?.hour} ${'hours'}` || 'Unknown',
		name: item?.hour ? moment(item.hour, 'HH').format('hh A') : 'Unknown',

		count: parseInt(item?.count, 10),
	}));

	const Top10TaskTypeChart = top10taskType?.result?.map(item => ({
		name: item?.req_taskType || 'Unknown',
		count: parseInt(item?.count, 10),
	}));

	const Bottom10TaskTypeChart = bottom10TaskType?.result?.map(item => ({
		name: item?.req_taskType || 'Unknown',
		count: item?.count,
	}));
	const LabourDeliveryChart = labourDelivery?.result?.map(item => ({
		name: item?.deliverytype || 'Others',
		count: parseInt(item?.totalcount, 10),
	}));

	const PatientAdmissionChart = patientAdmission?.result?.map(item => ({
		name: item?.health_state || 'Others',
		count: parseInt(item?.totalcount, 10),
	}));

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const renderCustomLabelAlt2 = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	const MetricCard = ({ loading, value, label, total, color = 'primary' }) => {
		const percentage = value != null && total > 0 ? ((value / total) * 100).toFixed(1) : 0;
		return (
			<div className="col-sm-3">
				<a className="element-box el-tablo" href="#">
					<div className="label">{label}</div>
					<div className={`value`}>{loading ? <TableLoading /> : value != null ? Number(value)?.toLocaleString() : '0'}</div>
					<div className={`trending ${percentage > 0 ? 'trending-up-basic' : 'trending-down-basic'}`}>
						<span>{percentage > 0 ? percentage : '0'}%</span>
						{percentage > 0 ? <i className="os-icon os-icon-arrow-up2"></i> : <i className="os-icon os-icon-arrow-down"></i>}
					</div>
				</a>
			</div>
		);
	};

	const metricsConfig = [
		{ key: 'totalRequests', label: 'Total Requests' },
		{ key: 'cancelledOnly', label: 'Cancelled Only' },
		{ key: 'cancelled', label: 'Cancelled' },
		{ key: 'approved', label: 'Approved' },
		{ key: 'notApproved', label: 'Not Approved' },
		{ key: 'filled', label: 'Filled' },
		{ key: 'notFilled', label: 'Not Filled' },
		{ key: 'substituted', label: 'Substituted' },
		{ key: 'notSubstituted', label: 'Not Substituted' },
		{ key: 'received', label: 'Received' },
		{ key: 'notReceived', label: 'Not Received' },
		{ key: 'deleted', label: 'Deleted' },
	];

	const totalValue = Number(requestMetrics?.totalRequests) || 0;

	const renderReport = () => {
		switch (activeTab) {
			case 'nurse':
				return (
					<div className="row ">
						{/* Overview Metrics */}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<div className="d-flex justify-content-between align-items-center">
									<h6 className="element-header">Key Performance Indicators</h6>
									<div className="form-inline justify-content-end mb-4">
										<TimeRangeDropdown chartId="requestMetrics" value={filters.requestMetrics.dateFilter} />
									</div>
								</div>
								<p className="chart-description mb-3 p-2 card">Summary metrics showing overall Nursing activity and engagement levels.</p>
								<div className="element-content">
									<div className="row">
										{loading.kpi ? (
											<TableLoading />
										) : (
											<div className="row">
												{metricsConfig.map(({ key, label, total }) => {
													const value = requestMetrics?.[key];
													return (
														<MetricCard
															key={key}
															label={label}
															// color={color}
															value={value}
															loading={false}
															total={totalValue}
														/>
													);
												})}
											</div>
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Patients requests*/}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header">Patient Requests (Top 10)</h6>

								<TimeRangeDropdown chartId="patientRequests" value={filters?.patientRequests?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Review the top 10 most requested clinical tasks by nurses, filtered by date range, to identify underutilized areas and
									improve resource planning efficiently.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.patientRequests ? (
													<TableLoading />
												) : PatientRequestsChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={PatientRequestsChart} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Requests by nurse */}
						<div className="col-sm-12 col-lg-6">
							<div className="element-wrapper">
								<h6 className="element-header"> Request By Nurse (Top 10) </h6>

								<TimeRangeDropdown chartId="top10NurseRequest" value={filters?.top10NurseRequest?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Explore the top 10 most requested clinical tasks by nurses, filtered by date range, to gain insights into demand trends
									and optimize nursing resource allocation effectively.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.top10NurseRequest ? (
													<TableLoading />
												) : Top10NurseRequestChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<AreaChart data={Top10NurseRequestChart?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Area
																	type="monotone"
																	dataKey="count"
																	stroke="#0088FE"
																	fill="#0088FE"
																	strokeWidth={2}
																	dot={{ r: 4 }}
																	activeDot={{ r: 6 }}
																>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '12px', fill: '#333' }}
																	/>
																</Area>
															</AreaChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Requests by nurse*/}
						<div className="col-sm-12 col-lg-6">
							<div className="element-wrapper">
								<h6 className="element-header"> Request by Nurse (Bottom 10) </h6>
								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModal(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown chartId="bottom10NurseRequest" value={filters?.bottom10NurseRequest?.dateFilter} />
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Review the bottom 10 least requested clinical tasks by nurses, filtered by date range, to identify underutilized areas and
									improve resource planning efficiently.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.bottom10NurseRequest ? (
													<TableLoading />
												) : Bottom10NurseRequestChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<AreaChart data={Bottom10NurseRequestChart?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Area
																	type="monotone"
																	dataKey="count"
																	stroke="#0088FE"
																	fill="#0088FE"
																	strokeWidth={2}
																	dot={{ r: 4 }}
																	activeDot={{ r: 6 }}
																>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '12px', fill: '#333' }}
																	/>
																</Area>
															</AreaChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
							<SlidingPane isOpen={modal}>
								<div className=" content-box">
									<div className=" d-flex justify-content-end w-100">
										<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
											&times;
										</button>
									</div>

									<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
										<TimeRangeDropdown chartId="bottom10NurseRequest" value={filters?.bottom10NurseRequest?.dateFilter} />
									</div>
									<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
										<div className="col-sm-12">
											<div className="element-wrapper">
												<h6 className="element-header">Request by Nurse </h6>

												<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
													High-level metrics showing overall metrics of reading over time.
												</p>
												<div className="element-box">
													{/* <TableLoading /> */}

													<Row gutter={16}>
														<Col span={8}>
															<Card>
																<Statistic title="Total" value={Bottom10NurseRequestChart?.length} suffix="" />
																<small className="text-muted">Total Clinical Tasks</small>
															</Card>
														</Col>
														{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
														{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
													</Row>
												</div>
											</div>
										</div>
									</div>

									<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
										<p className="chart-description card p-2">
											This chart ranks requested clinical tasks by nurses, filtered by date range, to gain insights into demand trends and
											optimize nursing resource allocation effectively.
										</p>
									</div>

									<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
										<div className=" card w-100 ">
											<div className="row">
												<div className="col-md-12 mb-4">
													<div className="card h-100">
														<div className="card-body">
															{loading.bottom10NurseRequest ? (
																<TableLoading />
															) : Bottom10NurseRequestChart?.length > 0 ? (
																<div style={{ width: '100%', height: 'auto' }}>
																	{Bottom10NurseRequestChart?.length > 45 ? (
																		<ResponsiveContainer width="100%" height={Bottom10NurseRequestChart?.length * 30}>
																			<AreaChart
																				data={Bottom10NurseRequestChart}
																				layout="vertical"
																				margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
																			>
																				<CartesianGrid strokeDasharray="3 3" />

																				{/* Category labels go on Y axis now */}
																				<YAxis
																					type="category"
																					dataKey="name"
																					tick={{ fontSize: 12 }}
																					width={100}
																					tickFormatter={value => {
																						const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																						const capitalized = truncated
																							.toLowerCase()
																							.split(' ')
																							.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																							.join(' ');
																						return capitalized;
																					}}
																				/>

																				{/* Values on X axis */}
																				<XAxis type="number" domain={[0, 'auto']} />

																				<Tooltip
																					formatter={value => Number(value).toLocaleString()}
																					labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																				/>

																				<Legend />

																				<Area
																					dataKey="count"
																					type="monotone"
																					stroke="#0088FE"
																					fill="#0088FE"
																					strokeWidth={2}
																					dot={{ r: 4 }}
																					activeDot={{ r: 6 }}
																				>
																					<LabelList
																						dataKey="count"
																						position="right"
																						content={renderCustomLabelAlt}
																						style={{ fontSize: '12px', fill: '#333' }}
																					/>
																				</Area>
																			</AreaChart>
																		</ResponsiveContainer>
																	) : (
																		<ResponsiveContainer>
																			<AreaChart data={Bottom10NurseRequestChart} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																				<CartesianGrid strokeDasharray="3 3" />
																				<XAxis
																					dataKey={'name'}
																					angle={-45}
																					textAnchor="end"
																					fontSize={12}
																					interval={0}
																					tickFormatter={value => {
																						const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																						const capitalized = truncated
																							.toLowerCase()
																							.split(' ')
																							.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																							.join(' ');
																						return capitalized;
																					}}
																				/>
																				<YAxis domain={[0, 'auto']} />
																				<Tooltip
																					formatter={value => Number(value).toLocaleString()}
																					labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																				/>
																				<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																				<Area
																					type="monotone"
																					dataKey="count"
																					stroke="#0088FE"
																					fill="#0088FE"
																					strokeWidth={2}
																					dot={{ r: 4 }}
																					activeDot={{ r: 6 }}
																				>
																					<LabelList
																						dataKey="count"
																						position="top"
																						content={renderCustomLabelAlt}
																						style={{ fontSize: '12px', fill: '#333' }}
																					/>
																				</Area>
																			</AreaChart>
																		</ResponsiveContainer>
																	)}
																</div>
															) : (
																<div className="text-center fs-bold">No Record Found!</div>
															)}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</SlidingPane>
						</div>

						{/* Patients requests*/}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header"> Top 10 Nursing services</h6>

								<TimeRangeDropdown chartId="patientRequests" value={filters?.patientRequests?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Review the top 10 most requested clinical services by nurses, filtered by date range, to identify underutilized areas and
									improve resource planning efficiently.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.patientRequests ? (
													<TableLoading />
												) : PatientRequestsChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={PatientRequestsChart} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Task By Nurse Top 10*/}
						<div className="col-sm-12 col-lg-6">
							<div className="element-wrapper">
								<h6 className="element-header"> Clinical Task By Nurse(Top 10) </h6>

								<TimeRangeDropdown chartId="top10NurseTask" value={filters?.top10NurseTask?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Review the bottom 10 least requested clinical tasks by nurses, filtered by date range, to identify underutilized areas and
									improve resource planning efficiently.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.top10NurseTask ? (
													<TableLoading />
												) : Top10NurseTaskChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={Top10NurseTaskChart?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		content={renderCustomLabelAlt}
																		position="top"
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Task By Nurse bottom 10*/}
						<div className="col-sm-12 col-lg-6">
							<div className="element-wrapper">
								<h6 className="element-header">Clinical Task By Nurse(Bottom 10) </h6>

								<div className="d-flex justify-content-end w-100">
									<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											onClick={() => setModalAlt(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
									&nbsp;
									<TimeRangeDropdown chartId="bottom10NurseTask" value={filters?.bottom10NurseTask?.dateFilter} />
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Review the bottom 10 least requested clinical tasks by nurses, filtered by date range, to identify underutilized areas and
									improve resource planning efficiently.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.bottom10NurseTask ? (
													<TableLoading />
												) : Bottom10NurseTaskChart.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={Bottom10NurseTaskChart?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	domain={[0, 'auto']}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		content={renderCustomLabelAlt}
																		position="top"
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>

								<SlidingPane isOpen={modalAlt}>
									<div className=" content-box">
										<div className=" d-flex justify-content-end w-100">
											<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
												&times;
											</button>
										</div>

										<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
											<TimeRangeDropdown chartId="bottom10NurseTask" value={filters?.bottom10NurseTask?.dateFilter} />
										</div>
										<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
											<div className="col-sm-12">
												<div className="element-wrapper">
													<h6 className="element-header">Request by Nurse </h6>

													<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
														High-level metrics showing overall metrics of reading over time.
													</p>
													<div className="element-box">
														{/* <TableLoading /> */}

														<Row gutter={16}>
															<Col span={8}>
																<Card>
																	<Statistic title="Total" value={Bottom10NurseTaskChart?.length} suffix="" />
																	<small className="text-muted">Total Clinical Tasks</small>
																</Card>
															</Col>
															{/* <Col span={8}>
														<Card>
															<Statistic
																title="Total Revenue"
																value={transSummary?.totalRevenueFormatted} suffix=""
															/>

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic
																title="Total Transactions"
																value={transSummary?.totalTransactions} suffix=""
															/>
															<Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/>
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col> */}
															{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
														</Row>
													</div>
												</div>
											</div>
										</div>

										<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
											<p className="chart-description card p-2">
												This chart ranks requested clinical tasks by nurses, filtered by date range, to identify underutilized areas and
												improve resource planning efficiently.
											</p>
										</div>

										<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
											<div className=" card w-100 ">
												<div className="row">
													<div className="col-md-12 mb-4">
														<div className="card h-100">
															<div className="card-body">
																{loading.bottom10NurseTask ? (
																	<TableLoading />
																) : Bottom10NurseTaskChart.length > 0 ? (
																	<div style={{ width: '100%', height: 'auto' }}>
																		{Bottom10NurseTaskChart?.length > 45 ? (
																			<ResponsiveContainer width="100%" height={Bottom10NurseTaskChart?.length * 30}>
																				<AreaChart
																					data={Bottom10NurseTaskChart}
																					layout="vertical"
																					margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
																				>
																					<CartesianGrid strokeDasharray="3 3" />

																					{/* Categories on Y axis */}
																					<YAxis
																						type="category"
																						dataKey="name"
																						tick={{ fontSize: 12 }}
																						width={100}
																						tickFormatter={value => {
																							const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																							const capitalized = truncated
																								.toLowerCase()
																								.split(' ')
																								.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																								.join(' ');
																							return capitalized;
																						}}
																					/>

																					{/* Values on X axis */}
																					<XAxis type="number" domain={[0, 'auto']} />

																					<Tooltip
																						formatter={value => Number(value).toLocaleString()}
																						labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																					/>
																					<Legend />

																					<Area
																						type="monotone"
																						dataKey="count"
																						stroke="#22BB33"
																						fill="#22BB33"
																						strokeWidth={2}
																						activeDot={{ r: 6 }}
																					>
																						<LabelList
																							dataKey="count"
																							position="right"
																							content={renderCustomLabelAlt}
																							style={{ fontSize: '10px', fill: '#333' }}
																						/>
																					</Area>
																				</AreaChart>
																			</ResponsiveContainer>
																		) : (
																			<ResponsiveContainer>
																				<LineChart data={Bottom10NurseTaskChart} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																					<CartesianGrid strokeDasharray="3 3" />
																					<XAxis
																						dataKey={'name'}
																						angle={-45}
																						textAnchor="end"
																						fontSize={12}
																						domain={[0, 'auto']}
																						interval={0}
																						tickFormatter={value => {
																							const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																							const capitalized = truncated
																								.toLowerCase()
																								.split(' ')
																								.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																								.join(' ');
																							return capitalized;
																						}}
																					/>
																					<YAxis domain={[0, 'auto']} />
																					<Tooltip
																						formatter={value => Number(value).toLocaleString()}
																						labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																					/>
																					<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																					<Line
																						type="monotone"
																						dataKey="count"
																						stroke="#22BB33"
																						strokeWidth={2}
																						dot={{ r: 4 }}
																						activeDot={{ r: 6 }}
																					>
																						<LabelList
																							dataKey="count"
																							content={renderCustomLabelAlt}
																							position="top"
																							style={{ fontSize: '10px', fill: '#333' }}
																						/>
																					</Line>
																				</LineChart>
																			</ResponsiveContainer>
																		)}
																	</div>
																) : (
																	<div className="text-center fs-bold">No Record Found!</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</SlidingPane>
							</div>
						</div>

						{/* Clinical Task Trends */}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header"> Clinical Task Trends </h6>

								<TimeRangeDropdown chartId="taskTrends" value={filters?.taskTrends?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									View the total count of clinical tasks by selecting a date range to track trends and optimize workflows
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.taskTrends ? (
													<TableLoading />
												) : TaskTrendChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={TaskTrendChart} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={filters?.taskTrends?.dateFilter === 'past-quarter' ? 2 : 0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#0088FE" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Patient Admission chart */}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header"> Labour Delivery Chart by Type</h6>

								<TimeRangeDropdown chartId="labourDelivery" value={filters?.labourDelivery?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									This chart shows the breakdown of labour and delivery cases by type, helping to understand the most common delivery
									methods and trends in maternal care.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.labourDelivery ? (
													<TableLoading />
												) : LabourDeliveryChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={LabourDeliveryChart} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#0088FE" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		content={renderCustomLabelAlt}
																		position="top"
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Patient Admission chart */}
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header"> Patient Admission Chart by health State </h6>

								<TimeRangeDropdown chartId="patientAdmission" value={filters?.patientAdmission?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									This chart shows patients health sate at the time of admission for the selected time range
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.patientAdmission ? (
													<TableLoading />
												) : PatientAdmissionChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={PatientAdmissionChart} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line type="monotone" dataKey="count" stroke="#0088FE" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
																	<LabelList
																		dataKey="count"
																		position="top"
																		content={renderCustomLabelAlt}
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Task By Hour overview*/}
						<div className="col-sm-12 ">
							<div className="element-wrapper">
								<h6 className="element-header">Clinical Task By Hour (Trends)</h6>

								<TimeRangeDropdown chartId="taskHour" value={filters?.taskHour?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									This chart shows the clinical task volumes per hour, for each 24-hour period, allowing for a detailed analysis of task for
									the selected time range.
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.taskHour ? (
													<TableLoading />
												) : TaskHourChart.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<LineChart data={TaskHourChart} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																<Line
																	type="monotone"
																	dataKey="count"
																	name="value"
																	stroke="#FFBB28"
																	strokeWidth={2}
																	dot={{ r: 4 }}
																	activeDot={{ r: 6 }}
																>
																	<LabelList
																		dataKey="count"
																		content={renderCustomLabelAlt2}
																		position="top"
																		style={{ fontSize: '10px', fill: '#333' }}
																	/>
																</Line>
															</LineChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Task By Type Top 10*/}
						<div className="col-sm-12 col-lg-12">
							<div className="element-wrapper">
								<h6 className="element-header">Clinical Tasks By Type </h6>

								<TimeRangeDropdown chartId="top10taskType" value={filters?.top10taskType?.dateFilter} />
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									This chart shows the volumes of clinical tasks by type, filtered by date range, to identify trends and enhance workflow
								</p>
								<div className="row">
									<div className="col-md-12 mb-4">
										<div className="card h-100">
											<div className="card-body">
												{loading.top10taskType ? (
													<TableLoading />
												) : Top10TaskTypeChart?.length > 0 ? (
													<div style={{ width: '100%', height: 400 }}>
														<ResponsiveContainer>
															<BarChart data={Top10TaskTypeChart} layout="horizontal" margin={{ top: 20, right: 60, left: 20, bottom: 50 }}>
																<CartesianGrid strokeDasharray="3 3" />
																<XAxis
																	dataKey={'name'}
																	angle={-45}
																	textAnchor="end"
																	fontSize={12}
																	interval={0}
																	tickFormatter={value => {
																		const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																		const capitalized = truncated
																			.toLowerCase()
																			.split(' ')
																			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																			.join(' ');
																		return capitalized;
																	}}
																/>
																<YAxis domain={[0, 'auto']} />
																<Tooltip
																	formatter={value => Number(value).toLocaleString()}
																	labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																/>
																<Legend wrapperStyle={{ marginBottom: '-50px' }} />
																<Bar dataKey="count" fill="#0088FE" name="Count">
																	<LabelList
																		dataKey="count"
																		position="top"
																		style={{ textTransform: 'capitalize', fontSize: '12px', fill: '#333' }}
																		content={formatBarLabel}
																	/>
																</Bar>
															</BarChart>
														</ResponsiveContainer>
													</div>
												) : (
													<div className="text-center fs-bold">No Record Found!</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				);
			case 'immunization':
				return <ImmunizationOverview />;
			case 'nicu':
				return <NICUOverview />;
			default:
				return <div>Select a report type</div>;
		}
	};

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="element-wrapper">
					<div className=" d-flex justify-content-between align-items-center">
						<h6 className="element-header">Nurse's Overview</h6>
						<AnalysisBystaff module="nurse" />
					</div>
					<div className="element-box">
						<div className="row mb-4">
							<div className="col-sm-8">
								<div className="btn-group" role="group">
									<button className={`btn btn-light ${activeTab === 'nurse' ? 'active' : ''}`} onClick={() => setActiveTab('nurse')}>
										Nurse
									</button>

									<button
										className={`btn btn-light ${activeTab === 'immunization' ? 'active' : ''}`}
										onClick={() => setActiveTab('immunization')}
									>
										Immunization
									</button>

									<button className={`btn btn-light ${activeTab === 'nicu' ? 'active' : ''}`} onClick={() => setActiveTab('nicu')}>
										NICU
									</button>
								</div>
							</div>
						</div>
						<div className="col-sm-12"></div>
						{renderReport()}
					</div>
				</div>
			</div>
		</div>
	);
};
export default NurseOverviewModal;
